(() => {
  const ROOT_SELECTOR =
    '[data-splitter="wrap"]';

  const HANDLE_SELECTOR =
    '[data-splitter="handle"]';

  const AFTER_SELECTOR =
    '[data-splitter="after"]';

  const DEFAULT_PERCENT = 50;
  const KEYBOARD_STEP = 1;
  const KEYBOARD_LARGE_STEP = 10;

  const DEPENDENCY_RETRY_LIMIT = 40;
  const DEPENDENCY_RETRY_DELAY = 100;

  let dependenciesRegistered = false;
  let dependencyRetryTimer = null;
  let dependencyRetryAttempts = 0;


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function hasDependencies() {
    return Boolean(
      window.gsap &&
      window.Draggable
    );
  }

  function registerDependencies() {
    if (!hasDependencies()) {
      return false;
    }

    if (!dependenciesRegistered) {
      gsap.registerPlugin(
        Draggable
      );

      dependenciesRegistered = true;
    }

    return true;
  }


  /* =========================================
     ROOT HELPERS
     ========================================= */

  function getSplitters(
    scope = document
  ) {
    const splitters = [];

    if (
      scope instanceof Element &&
      scope.matches(ROOT_SELECTOR)
    ) {
      splitters.push(scope);
    }

    if (scope.querySelectorAll) {
      splitters.push(
        ...scope.querySelectorAll(
          ROOT_SELECTOR
        )
      );
    }

    return splitters;
  }


  /* =========================================
     CREATE INSTANCE
     ========================================= */

  function createSplitter(splitter) {
    if (!registerDependencies()) {
      return null;
    }

    if (
      splitter._wamboltSplitter &&
      typeof splitter
        ._wamboltSplitter
        .destroy === 'function'
    ) {
      splitter
        ._wamboltSplitter
        .destroy();
    }

    const handle =
      splitter.querySelector(
        HANDLE_SELECTOR
      );

    const after =
      splitter.querySelector(
        AFTER_SELECTOR
      );

    if (
      !handle ||
      !after
    ) {
      return null;
    }

    const controller =
      new AbortController();

    const { signal } = controller;

    const initialValue =
      Number.parseFloat(
        splitter.getAttribute(
          'data-splitter-initial'
        )
      );

    let currentPercent =
      Number.isFinite(initialValue)
        ? initialValue
        : DEFAULT_PERCENT;

    currentPercent =
      gsap.utils.clamp(
        0,
        100,
        currentPercent
      );

    let bounds =
      splitter.getBoundingClientRect();

    let draggable = null;
    let resizeObserver = null;
    let resizeFrame = null;
    let destroyed = false;


    /* =========================================
       ACCESSIBILITY STATE
       ========================================= */

    function updateAccessibility() {
      const roundedPercent =
        Math.round(
          currentPercent
        );

      handle.setAttribute(
        'aria-valuenow',
        String(
          roundedPercent
        )
      );

      handle.setAttribute(
        'aria-valuetext',
        `${roundedPercent} percent`
      );
    }


    /* =========================================
       POSITION
       ========================================= */

    function setPositions(percent) {
      bounds =
        splitter
          .getBoundingClientRect();

      currentPercent =
        gsap.utils.clamp(
          0,
          100,
          percent
        );

      const positionX =
        (
          currentPercent /
          100
        ) *
        bounds.width;

      gsap.set(
        handle,
        {
          x: positionX,
          left: 'unset',
        }
      );

      gsap.set(
        after,
        {
          clipPath:
            `inset(0 0 0 ${currentPercent}%)`,
        }
      );

      if (
        draggable &&
        typeof draggable.update ===
          'function'
      ) {
        draggable.update();
      }

      updateAccessibility();
    }

    setPositions(
      currentPercent
    );


    /* =========================================
       DRAG
       ========================================= */

    draggable =
      Draggable.create(
        handle,
        {
          type: 'x',

          bounds:
            splitter,

          cursor:
            'ew-resize',

          activeCursor:
            'grabbing',

          onDrag() {
            bounds =
              splitter
                .getBoundingClientRect();

            if (bounds.width <= 0) {
              return;
            }

            currentPercent =
              gsap.utils.clamp(
                0,
                100,
                (
                  this.x /
                  bounds.width
                ) *
                  100
              );

            gsap.set(
              after,
              {
                clipPath:
                  `inset(0 0 0 ${currentPercent}%)`,
              }
            );

            updateAccessibility();
          },
        }
      )[0];


    /* =========================================
       KEYBOARD
       ========================================= */

    function handleKeydown(event) {
      let nextPercent =
        currentPercent;

      if (
        event.key ===
        'ArrowRight'
      ) {
        nextPercent +=
          KEYBOARD_STEP;
      } else if (
        event.key ===
        'ArrowLeft'
      ) {
        nextPercent -=
          KEYBOARD_STEP;
      } else if (
        event.key ===
        'PageUp'
      ) {
        nextPercent +=
          KEYBOARD_LARGE_STEP;
      } else if (
        event.key ===
        'PageDown'
      ) {
        nextPercent -=
          KEYBOARD_LARGE_STEP;
      } else if (
        event.key ===
        'Home'
      ) {
        nextPercent = 0;
      } else if (
        event.key ===
        'End'
      ) {
        nextPercent = 100;
      } else {
        return;
      }

      event.preventDefault();

      setPositions(
        nextPercent
      );
    }

    handle.addEventListener(
      'keydown',
      handleKeydown,
      {
        signal,
      }
    );


    /* =========================================
       RESIZE
       ========================================= */

    function scheduleResize() {
      if (
        destroyed ||
        resizeFrame !== null
      ) {
        return;
      }

      resizeFrame =
        window.requestAnimationFrame(
          () => {
            resizeFrame = null;

            if (
              destroyed ||
              !splitter.isConnected
            ) {
              return;
            }

            setPositions(
              currentPercent
            );
          }
        );
    }

    window.addEventListener(
      'resize',
      scheduleResize,
      {
        passive: true,
        signal,
      }
    );

    if (
      'ResizeObserver' in window
    ) {
      resizeObserver =
        new ResizeObserver(
          scheduleResize
        );

      resizeObserver.observe(
        splitter
      );
    }


    /* =========================================
       INSTANCE
       ========================================= */

    const instance = {
      destroy() {
        if (destroyed) {
          return;
        }

        destroyed = true;

        controller.abort();

        if (resizeFrame !== null) {
          window.cancelAnimationFrame(
            resizeFrame
          );

          resizeFrame = null;
        }

        if (draggable) {
          draggable.kill();
          draggable = null;
        }

        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }

        gsap.set(
          handle,
          {
            clearProps:
              'transform,left',
          }
        );

        gsap.set(
          after,
          {
            clearProps:
              'clipPath',
          }
        );

        delete splitter
          ._wamboltSplitter;
      },
    };

    splitter._wamboltSplitter =
      instance;

    return instance;
  }


  /* =========================================
     COMPONENT LIFECYCLE
     ========================================= */

  function initBeforeAfterSplitSliders(
    scope = document
  ) {
    const splitters =
      getSplitters(scope);

    if (!splitters.length) {
      return true;
    }

    if (!registerDependencies()) {
      return false;
    }

    splitters.forEach(
      createSplitter
    );

    return true;
  }

  function destroyBeforeAfterSplitSliders(
    scope = document
  ) {
    getSplitters(scope).forEach(
      (splitter) => {
        if (
          splitter._wamboltSplitter &&
          typeof splitter
            ._wamboltSplitter
            .destroy === 'function'
        ) {
          splitter
            ._wamboltSplitter
            .destroy();
        }
      }
    );
  }


  /* =========================================
     DEPENDENCY-SAFE START
     ========================================= */

  function clearDependencyRetry() {
    if (
      dependencyRetryTimer !== null
    ) {
      window.clearTimeout(
        dependencyRetryTimer
      );

      dependencyRetryTimer =
        null;
    }
  }

  function scheduleDependencyRetry() {
    if (
      dependencyRetryTimer !== null
    ) {
      return;
    }

    if (
      dependencyRetryAttempts >=
      DEPENDENCY_RETRY_LIMIT
    ) {
      console.warn(
        'Wambolt Before/After could not initialize because GSAP or Draggable is unavailable.'
      );

      return;
    }

    dependencyRetryAttempts += 1;

    dependencyRetryTimer =
      window.setTimeout(
        () => {
          dependencyRetryTimer = null;

          if (
            initBeforeAfterSplitSliders(
              document
            )
          ) {
            dependencyRetryAttempts = 0;

            clearDependencyRetry();

            return;
          }

          scheduleDependencyRetry();
        },
        DEPENDENCY_RETRY_DELAY
      );
  }

  function start() {
    if (
      initBeforeAfterSplitSliders(
        document
      )
    ) {
      dependencyRetryAttempts = 0;

      clearDependencyRetry();

      return;
    }

    scheduleDependencyRetry();
  }


  /* =========================================
     INITIAL LOAD
     ========================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      }
    );
  } else {
    start();
  }


  /* =========================================
     SHOPIFY / BARBA LIFECYCLE
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (
        !initBeforeAfterSplitSliders(
          event.target
        )
      ) {
        scheduleDependencyRetry();
      }
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyBeforeAfterSplitSliders(
        event.target
      );
    }
  );
})();